import { lazy, object, string } from "yup";

import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

// Utility to convert phone code to country code (e.g., '234' → 'NG') 
const getCountryByPhoneCode = (phoneCode) => {
  const countries = getCountries();

  for (const country of countries) {
    const code = getCountryCallingCode(country);
    if (code === String(phoneCode)) {
      return country;
    }
  }

  return undefined;
};

export const isPhoneValid = (phone, phoneCode) => {
  const country = getCountryByPhoneCode(phoneCode);
  if (!country) return false;
  const phoneNumber = phone.startsWith("+")
    ? parsePhoneNumberFromString(phone)
    : parsePhoneNumberFromString(phone, country);
  return phoneNumber?.isValid() ?? false;
};

export const registerationSchema = (isFunder, isIndividual) =>
  object().shape({
    companyTypeId: string().required("Company type is required").trim(),
    companyName: !isFunder
      ? string()
          .required("Company name is required")
          .trim()
          .min(3, "Company Name should be greater than 2 characters")
          .max(60, "Company Name shouldn't be greater than 60 characters")
      : string().notRequired().nullable(),
    investorType: isFunder
      ? string().required("Investor type is required").trim()
      : string().notRequired(),
    investorName: isFunder
      ? string().required("Investor name is required").trim()
      : string().notRequired(),
    clientTitle:
      isFunder && isIndividual
        ? string().required("Client title is required.").trim()
        : string().notRequired().nullable(),
    clientType:
      isFunder && isIndividual
        ? string().required("Client type is required.").trim()
        : string().notRequired().nullable(),
    bvn:
      isFunder && isIndividual
        ? string()
            .required("BVN is required")
            .trim()
            .min(11, "Must be 11 digits")
            .max(11, "Must be 11 digits")
        : string().notRequired().nullable(),
    regno: isIndividual
      ? string()
          .notRequired()
          .nullable()
          .min(8, "Not less than 8 digits")
          .max(10, "Not greater than 10 digits")
      : string()
          .required("Reg no is required")
          .min(8, "Not less than 8 digits")
          .max(10, "Not greater than 10 digits"),
    //vat id modified
    vatId: isFunder
      ? string().notRequired().nullable().max(40, "Must not exceed 40 characters")
      : string().required("VAT ID is required").trim().min(1, "Must be at least 1 character").max(40, "Must not exceed 40 characters"),
    tradingName: string().required("Trading name is required").trim(),
    taxNum: string()
      .required("Tax number is required")
      .trim()
      .min(11, "Must be 11 alpha numeric characters")
      .max(11, "Must be 11 alpha numeric characters"),
    email: string()
      .required("Email is required")
      .email("Email is invalid")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        "Email must be a valid format like user@example.com"
      )
      .trim(),
    website: string().notRequired().nullable().url("Website is invalid"),
    contactPerson: string().required("Contact person is required").trim(),
    officePhone: lazy((_, { parent }) =>
      string()
        .required("Phone number is required")
        .test("is-valid-phone", "Invalid phone number", function (value) {
          const phoneCode = parent.officePhoneCode?.replace("+", "");
          return isPhoneValid(value || "", phoneCode);
        })
    ),
    contactEmail: string()
      .required("Email is required")
      .email("Email is invalid")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        "Email must be a valid format like user@example.com"
      )
      .trim(),
    contactPerson2: string().required("Contact person is required").trim(),
    officePhone2: lazy((_, { parent }) =>
      string()
        .required("Phone number is required")
        .test("is-valid-phone", "Invalid phone number", function (value) {
          const phoneCode = parent.officePhoneCode2?.replace("+", "");
          return isPhoneValid(value || "", phoneCode);
        })
    ),
    contactEmail2: string()
      .email("Email is invalid")
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        "Email must be a valid format like user@example.com"
      )
      .required("Email is required")
      .trim(),
  });